// Block Jam - DOM rendering, touch/drag interaction, and the GameDef export.

import { el, clear } from "../../lib/dom";
import { sfx, haptic } from "../../lib/feedback";
import type { GameDef, LevelView, LevelViewOptions } from "../types";
import levelsData from "./levels.json";
import { generateLevel, optionsForLevel } from "./generator";
import {
  type Block,
  type Dir,
  type Level,
  type Move,
  type State,
  allowedDirs,
  applyMove,
  cloneLevelState,
  hintMove,
  isFrozen,
  isSolved,
  legalMoves,
  movesForBlock,
  slideInfo,
} from "./engine";
import "./blockjam.css";

const LEVELS = levelsData as Level[];

// The campaign is endless: levels 1..40 come pre-generated and validated from
// levels.json (instant load); beyond that each level is generated on demand,
// deterministically from its number, and verified solvable by the generator.
const generatedCache = new Map<number, Level>();
function levelForNumber(n: number): Level {
  if (n <= LEVELS.length) return LEVELS[n - 1];
  let level = generatedCache.get(n);
  if (!level) {
    level = generateLevel(optionsForLevel(n)).level;
    generatedCache.set(n, level);
  }
  return level;
}

const DIR_VEC: Record<Dir, [number, number]> = {
  up: [-1, 0],
  down: [1, 0],
  left: [0, -1],
  right: [0, 1],
};

// Per-element pending CSS-transition reset timers, so a new gesture/animation on
// the same block cancels the previous fire-and-forget cleanup instead of racing it.
const nodeTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();
function clearNodeTimer(node?: HTMLElement | null): void {
  if (node && nodeTimers.has(node)) {
    clearTimeout(nodeTimers.get(node)!);
    nodeTimers.delete(node);
  }
}
function setNodeTimer(node: HTMLElement | null | undefined, fn: () => void, ms: number): void {
  if (!node) return;
  clearNodeTimer(node);
  nodeTimers.set(
    node,
    setTimeout(() => {
      nodeTimers.delete(node);
      fn();
    }, ms),
  );
}

function starsFor(moves: number, par: number): number {
  if (moves <= par) return 3;
  if (moves <= Math.ceil(par * 1.6)) return 2;
  return 1;
}

function createLevelView(opts: LevelViewOptions): LevelView {
  const level = levelForNumber(opts.level);
  const par = level.par ?? Math.max(3, level.blocks.length);

  let state: State = cloneLevelState(level);
  let moves = 0;
  let cell = 48;
  let finished = false;
  const blockEls = new Map<number, HTMLElement>();

  const root = opts.mount;
  clear(root);
  root.classList.add("bj");

  const board = el("div.bj-board");
  const stage = el("div.bj-stage", {}, board);
  const hint = el("div.bj-hint");

  const moveLabel = el("span.bj-moves");
  const toolbar = el(
    "div.bj-toolbar",
    {},
    el("button.bj-btn", { onclick: doHint, "aria-label": "Hint" }, "💡 Hint"),
    moveLabel,
    el("button.bj-btn", { onclick: doRestart, "aria-label": "Opnieuw" }, "↺ Opnieuw"),
  );

  root.append(stage, toolbar);

  // ---- Layout ----
  // Fit the board to the stage's *current* box on both axes. We clamp only the
  // upper bound (max cell size) so the board can always shrink to fit a small
  // screen instead of overflowing it; the lower bound is just a guard against a
  // transient 0 measurement, not a real minimum that could force an overflow.
  function computeCell(): void {
    const availW = stage.clientWidth || root.clientWidth || 360;
    const availH = stage.clientHeight || availW;
    // Reserve room for the gate tabs (stick out ~12px on every side) and the
    // board's drop-shadow ring so nothing gets clipped at the screen edge.
    const pad = 18;
    const byW = (availW - pad * 2) / level.w;
    const byH = (availH - pad * 2) / level.h;
    cell = Math.max(16, Math.min(72, Math.floor(Math.min(byW, byH))));
  }

  function render(): void {
    computeCell();
    board.style.width = `${level.w * cell}px`;
    board.style.height = `${level.h * cell}px`;
    board.style.setProperty("--cell", `${cell}px`);
    clear(board);
    blockEls.clear();

    // Grid background cells.
    for (let r = 0; r < level.h; r++) {
      for (let c = 0; c < level.w; c++) {
        const g = el("div.bj-cell");
        g.style.left = `${c * cell}px`;
        g.style.top = `${r * cell}px`;
        g.style.width = `${cell}px`;
        g.style.height = `${cell}px`;
        board.append(g);
      }
    }

    // Gates around the border.
    for (const gate of level.gates) {
      const tab = el(`div.bj-gate.c-${gate.color}.side-${gate.side}`);
      if (gate.side === "top") {
        tab.style.left = `${gate.index * cell}px`;
        tab.style.top = `${-12}px`;
        tab.style.width = `${cell}px`;
      } else if (gate.side === "bottom") {
        tab.style.left = `${gate.index * cell}px`;
        tab.style.top = `${level.h * cell - 2}px`;
        tab.style.width = `${cell}px`;
      } else if (gate.side === "left") {
        tab.style.top = `${gate.index * cell}px`;
        tab.style.left = `${-12}px`;
        tab.style.height = `${cell}px`;
      } else {
        tab.style.top = `${gate.index * cell}px`;
        tab.style.left = `${level.w * cell - 2}px`;
        tab.style.height = `${cell}px`;
      }
      board.append(tab);
    }

    // Static wall cells.
    for (const [wr, wc] of level.walls ?? []) {
      const wall = el("div.bj-wall");
      wall.style.left = `${wc * cell}px`;
      wall.style.top = `${wr * cell}px`;
      wall.style.width = `${cell}px`;
      wall.style.height = `${cell}px`;
      board.append(wall);
    }

    // Blocks.
    for (const b of state) board.append(makeBlockEl(b));
    board.append(hint);
    positionAll();
    refreshIce();
  }

  function makeBlockEl(b: Block): HTMLElement {
    const node = el(`div.bj-block.c-${b.color}.axis-${b.o}`, { "data-id": b.id });
    node.append(el("span.bj-grip"), el("span.bj-arrow", {}, b.o === "h" ? "⇄" : "⇅"));
    if (b.melt) node.append(el("span.bj-ice-badge"));
    blockEls.set(b.id, node);
    node.addEventListener("pointerdown", (e) => onPointerDown(e, b));
    return node;
  }

  /** Update frozen visuals: frost overlay + "exits still needed" badge; thaw when melted. */
  function refreshIce(): void {
    const exited = level.blocks.length - state.length;
    for (const b of state) {
      const node = blockEls.get(b.id);
      if (!node || !b.melt) continue;
      const remaining = b.melt - exited;
      const badge = node.querySelector(".bj-ice-badge");
      if (remaining > 0) {
        node.classList.add("frozen");
        if (badge) badge.textContent = `❄${remaining}`;
      } else {
        if (node.classList.contains("frozen")) {
          node.classList.remove("frozen");
          node.classList.add("thawed");
          setTimeout(() => node.classList.remove("thawed"), 600);
        }
        badge?.remove();
      }
    }
  }

  function positionBlock(b: Block): void {
    const node = blockEls.get(b.id);
    if (!node) return;
    node.style.left = `${b.c * cell}px`;
    node.style.top = `${b.r * cell}px`;
    node.style.width = `${(b.o === "h" ? b.len : 1) * cell}px`;
    node.style.height = `${(b.o === "v" ? b.len : 1) * cell}px`;
    node.style.transform = "translate(0,0)";
  }

  function positionAll(): void {
    for (const b of state) positionBlock(b);
  }

  // ---- Drag interaction ----
  interface Drag {
    block: Block;
    startX: number;
    startY: number;
    axis: "h" | "v" | null;
    delta: number;
    neg: { steps: number; exit: boolean };
    pos: { steps: number; exit: boolean };
  }
  let drag: Drag | null = null;

  function onPointerDown(e: PointerEvent, b: Block): void {
    if (finished) return;
    if (drag) return; // ignore a second pointer while a drag is in progress
    const live = state.find((x) => x.id === b.id);
    if (!live) return;
    e.preventDefault();
    if (isFrozen(level, state, live)) {
      // Frozen: shake to show it can't move yet.
      const frozenNode = blockEls.get(live.id);
      frozenNode?.classList.add("shake");
      setTimeout(() => frozenNode?.classList.remove("shake"), 350);
      sfx.error();
      haptic(20);
      return;
    }
    const dirs = allowedDirs(live);
    // Cancel any pending transition-reset and clear leftover transition so the
    // new drag starts clean.
    const startNode = blockEls.get(b.id);
    clearNodeTimer(startNode);
    if (startNode) startNode.style.transition = "";
    const fixedAxis = live.o; // every block is axis-locked now
    drag = {
      block: live,
      startX: e.clientX,
      startY: e.clientY,
      axis: fixedAxis,
      delta: 0,
      neg: { steps: 0, exit: false },
      pos: { steps: 0, exit: false },
    };
    if (fixedAxis) prepareAxis(fixedAxis, dirs);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    blockEls.get(b.id)?.classList.add("dragging");
  }

  function prepareAxis(axis: "h" | "v", dirs: Dir[]): void {
    if (!drag) return;
    const negDir: Dir = axis === "h" ? "left" : "up";
    const posDir: Dir = axis === "h" ? "right" : "down";
    drag.neg = dirs.includes(negDir) ? slideInfo(level, state, drag.block, negDir) : { steps: 0, exit: false };
    drag.pos = dirs.includes(posDir) ? slideInfo(level, state, drag.block, posDir) : { steps: 0, exit: false };
  }

  function onPointerMove(e: PointerEvent): void {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.axis) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
      drag.axis = Math.abs(dx) >= Math.abs(dy) ? "h" : "v";
      prepareAxis(drag.axis, allowedDirs(drag.block));
    }
    const raw = drag.axis === "h" ? dx : dy;
    const maxNeg = drag.neg.steps * cell + (drag.neg.exit ? cell : 0);
    const maxPos = drag.pos.steps * cell + (drag.pos.exit ? cell : 0);
    drag.delta = Math.max(-maxNeg, Math.min(maxPos, raw));
    const node = blockEls.get(drag.block.id);
    if (node) {
      node.style.transform =
        drag.axis === "h" ? `translate(${drag.delta}px,0)` : `translate(0,${drag.delta}px)`;
    }
  }

  function onPointerUp(): void {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    if (!drag) return;
    const d = drag;
    const node = blockEls.get(d.block.id);
    node?.classList.remove("dragging");
    drag = null;
    if (!d.axis) return;

    const negDir: Dir = d.axis === "h" ? "left" : "up";
    const posDir: Dir = d.axis === "h" ? "right" : "down";

    if (d.delta > 0) {
      if (d.pos.exit && d.delta > d.pos.steps * cell + cell * 0.35) return exitBlock(d.block, posDir);
      const steps = Math.min(d.pos.steps, Math.round(d.delta / cell));
      if (steps > 0) return slideBlock(d.block, posDir, steps);
    } else if (d.delta < 0) {
      if (d.neg.exit && -d.delta > d.neg.steps * cell + cell * 0.35) return exitBlock(d.block, negDir);
      const steps = Math.min(d.neg.steps, Math.round(-d.delta / cell));
      if (steps > 0) return slideBlock(d.block, negDir, steps);
    }
    // No valid move: snap back.
    if (node) {
      node.style.transition = "transform .18s ease";
      node.style.transform = "translate(0,0)";
      setNodeTimer(node, () => (node.style.transition = ""), 180);
    }
  }

  function commit(move: Move): void {
    state = applyMove(state, move);
    moves++;
    updateHud();
    refreshIce(); // exits may have thawed an ice block
    if (isSolved(state)) return win();
    if (legalMoves(level, state).length === 0) return stuck();
  }

  function slideBlock(b: Block, dir: Dir, steps: number): void {
    if (steps <= 0) return;
    // Honor exactly how far the player dragged (steps is already clamped to the
    // max free slide in onPointerUp), not the full maximal slide.
    const [dr, dc] = DIR_VEC[dir];
    const mv: Move = { blockId: b.id, dir, type: "slide", to: { r: b.r + dr * steps, c: b.c + dc * steps } };
    sfx.slide();
    haptic(8);
    const node = blockEls.get(b.id);
    if (node) {
      node.style.transition = "left .16s ease, top .16s ease, transform .16s ease";
      node.style.transform = "translate(0,0)";
    }
    b.r = mv.to!.r;
    b.c = mv.to!.c;
    positionBlock(b);
    setNodeTimer(node, () => node && (node.style.transition = ""), 170);
    commit(mv);
  }

  function exitBlock(b: Block, dir: Dir): void {
    const mv = movesForBlock(level, state, b).find((m) => m.dir === dir && m.type === "exit");
    if (!mv) return;
    sfx.clear();
    haptic([10, 30]);
    const node = blockEls.get(b.id);
    if (node) {
      const fly: Record<Dir, string> = {
        up: `translate(0,${-cell * (level.h + 1)}px)`,
        down: `translate(0,${cell * (level.h + 1)}px)`,
        left: `translate(${-cell * (level.w + 1)}px,0)`,
        right: `translate(${cell * (level.w + 1)}px,0)`,
      };
      node.style.transition = "transform .28s ease-in, opacity .28s ease-in";
      node.style.transform = fly[dir];
      node.style.opacity = "0";
      setTimeout(() => {
        node.remove();
        // Only unmap the element this timer owns - a restart may have re-rendered
        // and registered a fresh element under the same block id in the meantime.
        if (blockEls.get(b.id) === node) blockEls.delete(b.id);
      }, 290);
    }
    commit(mv);
  }

  // ---- Hint ----
  function doHint(): void {
    if (finished) return;
    const blockId = hintMove(level, state);
    if (blockId == null) return;
    const node = blockEls.get(blockId);
    if (!node) return;
    sfx.tap();
    node.classList.add("hinted");
    setTimeout(() => node.classList.remove("hinted"), 1200);
  }

  /** Abort an in-progress drag (e.g. when the level restarts mid-gesture). */
  function cancelDrag(): void {
    if (!drag) return;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    blockEls.get(drag.block.id)?.classList.remove("dragging");
    drag = null;
  }

  function doRestart(): void {
    cancelDrag(); // a lingering pointerup must not commit a stale move onto the fresh state
    state = cloneLevelState(level);
    moves = 0;
    finished = false;
    overlay?.remove();
    overlay = null;
    updateHud();
    render();
  }

  // ---- Win / stuck ----
  let overlay: HTMLElement | null = null;

  let winTimer: ReturnType<typeof setTimeout> | null = null;

  function win(): void {
    if (finished) return;
    finished = true;
    const stars = starsFor(moves, par);
    sfx.win();
    haptic([20, 40, 20]);
    winTimer = setTimeout(() => opts.onWin({ stars, moves }), 350);
  }

  function stuck(): void {
    if (finished) return;
    finished = true;
    sfx.lose();
    overlay = el(
      "div.bj-overlay",
      {},
      el(
        "div.bj-overlay-card",
        {},
        el("h3", {}, "Vastgelopen!"),
        el("p", {}, "Er zijn geen zetten meer mogelijk."),
        el("button.bj-btn.primary", { onclick: doRestart }, "↺ Opnieuw proberen"),
        el("button.bj-btn", { onclick: opts.requestExit }, "Terug naar levels"),
      ),
    );
    stage.append(overlay);
  }

  function updateHud(): void {
    moveLabel.textContent = `Zetten: ${moves} · Par ${par}`;
    opts.setStatus?.(`${state.length} blokken`);
  }

  // ---- Init ----
  updateHud();
  render();
  // The router mounts this screen into the DOM only AFTER createLevelView
  // returns, so the render() above measures a still-detached, 0-height stage and
  // falls back to an over-sized estimate. Re-fit on the next task, by which time
  // we're attached and the stage has a real size. (setTimeout, not rAF, so it
  // still runs when the tab isn't actively painting.) This is what fixes the
  // board being too big / clipped on first load.
  const settle = setTimeout(() => {
    if (!drag) render();
  }, 0);
  // Keep re-fitting on later size changes: the device rotating, or the mobile
  // address bar showing/hiding (which resizes the dvh viewport).
  let lastW = -1;
  let lastH = -1;
  const ro = new ResizeObserver(() => {
    if (drag) return; // never rebuild mid-gesture
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    if (w === lastW && h === lastH) return; // ignore no-op callbacks
    lastW = w;
    lastH = h;
    render();
  });
  ro.observe(stage);

  return {
    destroy() {
      cancelDrag();
      if (winTimer) clearTimeout(winTimer);
      clearTimeout(settle);
      ro.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    },
    restart: doRestart,
  };
}

export const blockJam: GameDef = {
  id: "blockjam",
  title: "Block Jam",
  tagline: "Schuif de blokken naar de juiste poort",
  accent: "#10b981",
  icon: "🧩",
  levelCount: Infinity,
  createLevelView,
};
